import './ProviderSearch.css';
import './workQueue/workQueue.css';

import ColumnFilter, { FilterSection } from './workQueue/ColumnFilter';
import { HiddenFilters, NONE, describeHidden, shown, tallyOptions } from './workQueue/queueFilters';
import { NoteHistory, OwnerCell, WorkItemBadges, WorkItemForm } from './workQueue/WorkItemForm';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  WorkItemAnnotation,
  WorkItemNote,
  WorkItemSubmission,
  exclusionLabel,
  rollForward,
  statusLabel,
  statusRank,
  teamLabel,
  useWorkQueueLists
} from './workQueue/workQueueLists';

import GuideBanner from './GuideBanner';
import GuideLink from './GuideLink';
import axios from 'axios';
import { useDataEnvironment } from '../dataEnvironment';
import { useNavigate } from 'react-router-dom';

interface EpicBlockProviderRow {
  npi: string;
  serId: string | null;
  providerName: string | null;
  status: string;
  primaryReason: string;
  /** Last gate this provider hits; what remains after everything upstream is fixed. */
  furthestReason?: string | null;
  distinctReasons: string[];
  comboCount: number;
  passCount: number;
  deptCount?: number | null;
  /** Level 2 category, present only where the deep probe ran. */
  deepPrimaryReason?: string | null;
  deepComboCount?: number;
  /** Visit types Epic named as block-mismatched. */
  blockedVisitTypes?: string[];
  snapshotDate: string;
  directoryActive: boolean | null;
  /** On the Open Scheduling roster as the newest import defined it. */
  onRoster?: boolean;
  blockedPublicDepartments: string[];
  blockedDepartmentIds?: string[];
  excludedDepartmentIds?: string[];
  effectiveBlockedDepartmentIds?: string[];
  /** Departments where this provider has a Level 2 Epic build problem. */
  deepDepartmentIds?: string[];
  /** An open department item already covers one of this provider's departments. */
  coveredByDepartmentItem?: { departmentId: string; status: string } | null;
  /** Status once departments that are never bookable are set aside. */
  effectiveStatus?: string;
  /** Whether the public site can show the Epic slots; null when Epic has none. */
  publicReach?: PublicReach | null;
  priority?: 'P1' | 'P2' | 'P3' | null;
  priorityReason?: string | null;
  maintenanceOnly?: boolean;
  statusLabel?: string;
  primaryReasonLabel?: string;
  /** Rolled-up work-queue notes; null until someone annotates the provider. */
  annotation?: WorkItemAnnotation | null;
}

interface WorkQueueNote extends WorkItemNote {
  npi: string;
  providerPrimaryReason: string | null;
}

type PriorityTier = 'P1' | 'P2' | 'P3';

type PublicReach = 'PUBLIC' | 'NOT-ON-LOCATION' | 'NO-LOCATION' | 'UNVERIFIED';

const PUBLIC_REACH: Record<PublicReach, { label: string; description: string }> = {
  PUBLIC: {
    label: 'Bookable on the public site',
    description: 'Epic returns slots at a department on one of this provider\'s published Marketing Master locations.'
  },
  'NOT-ON-LOCATION': {
    label: 'Slots not on a published location',
    description: 'Bookable in Epic, but only at departments that are not on this provider\'s published Marketing Master locations, so Find Care cannot show the slots. Symplr location work, not Epic work.'
  },
  'NO-LOCATION': {
    label: 'No published location',
    description: 'Bookable in Epic, but the provider has no Marketing Master location with an Epic department in Symplr, so nothing can surface publicly.'
  },
  UNVERIFIED: {
    label: 'Not verified',
    description: 'Epic reports slots, but the scan recorded no slot department, so public reach cannot be checked.'
  }
};

const NOT_BOOKABLE_IN_EPIC = 'Not bookable in Epic';

const isPublishingGap = (provider: EpicBlockProviderRow): boolean =>
  provider.publicReach === 'NOT-ON-LOCATION' || provider.publicReach === 'NO-LOCATION';

/** P3 held back by the department-level flag: one change on the department clears it. */
const isDepartmentConfig = (provider: EpicBlockProviderRow): boolean =>
  provider.priority === 'P3' && provider.distinctReasons.includes('DEPARTMENT-FLAG-OFF');

interface PriorityDefinition {
  tier: PriorityTier;
  label: string;
  description: string;
}

/** Glossary entry from the server dictionary; the audit table is the only place
 *  these codes surface, so the definition has to be reachable from the cell. */
interface CodeDefinition {
  code: string;
  label: string;
  description: string;
  remediation: string | null;
  epicReference: string | null;
  /** 1 = configuration cascade, 2 = deep slot-search probe. */
  level?: 1 | 2;
  /** Team the workbook names as owning the fix. Level 2 only. */
  owner?: string;
  test?: string;
}

interface ExcludedDepartment {
  departmentId: string;
  name: string;
  rationale: string;
  evidence: string;
  excludedUntil?: string | null;
}

/** Level 2 - what the deep slot-search probe found for one pairing. */
interface EpicBlockComboDeepProbe {
  reasonCode: string | null;
  templateSlotsFound: number | null;
  firstTemplateSlotDate: string | null;
  daysScanned: number | null;
  autoSchedErrCode: number | null;
  autoSchedErrMessage: string | null;
  autoSchedErrDays: number | null;
  openSchedErrCode: string | null;
  openSchedErrDetails: string | null;
  checkError: string | null;
}

interface EpicBlockCombo {
  departmentId: string | null;
  departmentName: string | null;
  visitTypeId: string | null;
  visitTypeName: string | null;
  systemEnabled: boolean | null;
  providerFlagOK: boolean | null;
  deptFlagOK: boolean | null;
  visitTypeSchedulable: boolean | null;
  dateWindowOK: boolean | null;
  templateOrBlockOK: boolean | null;
  hasFutureTemplate: boolean | null;
  slotsFound: number | null;
  reasonCode: string;
  failingLayers: string[];
  departmentExcluded?: boolean;
  /** Null when the mapping was unavailable or the pairing has no department. */
  publication?: 'PUBLISHED' | 'OTHER-PROVIDERS' | 'NOT-PUBLISHED' | null;
  reasonDetail?: { label: string; description: string; remediation: string | null; blocking: boolean };
  deep?: EpicBlockComboDeepProbe | null;
  deepReasonDetail?: CodeDefinition | null;
}

interface EpicBlockDetail {
  npi: string;
  providerName: string | null;
  status: string;
  primaryReason: string;
  snapshotDate: string;
  combos: EpicBlockCombo[];
  statusDetail?: { label: string; description: string };
  primaryReasonDetail?: { label: string; description: string; remediation: string | null };
}

interface SnapshotManifest {
  snapshotDate: string;
  providerCount: number;
  comboCount: number;
  /** Pairings the Level 2 probe re-examined; absent before the 09/04/2026 format. */
  deepComboCount?: number;
  sourceFile: string | null;
  source: 'xlsx' | 'api';
  statusCounts: Record<string, number>;
}

interface DeltaRow {
  npi: string;
  providerName: string | null;
  fromStatus?: string;
  toStatus?: string;
  status?: string;
  fromReasonLabel?: string;
  toReasonLabel?: string;
}

interface SnapshotDelta {
  from: string | null;
  to: string | null;
  newlyBlocked: DeltaRow[];
  newlyAvailable: DeltaRow[];
  reasonChanged: DeltaRow[];
  added: DeltaRow[];
  removed: DeltaRow[];
}

type DeltaCategory = 'newlyBlocked' | 'newlyAvailable' | 'reasonChanged' | 'added' | 'removed';

const DELTA_CATEGORIES: { key: DeltaCategory; label: string; tone: 'negative' | 'positive' | 'neutral' }[] = [
  { key: 'newlyBlocked', label: 'Lost slots', tone: 'negative' },
  { key: 'newlyAvailable', label: 'Gained slots', tone: 'positive' },
  { key: 'reasonChanged', label: 'Reason changed', tone: 'neutral' },
  { key: 'added', label: 'New to extract', tone: 'neutral' },
  { key: 'removed', label: 'Dropped from extract', tone: 'neutral' }
];

/**
 * The movement tiles kept on the working page. Extract membership churn moved
 * off it: the executive dashboard now carries the summary story, and those two
 * counts are suppressed anyway whenever a snapshot did not cover its full
 * roster, so a tile for them would usually read zero for the wrong reason.
 */
const DELTA_TILES = DELTA_CATEGORIES.filter(
  (category) => category.key !== 'added' && category.key !== 'removed'
);

type SortField = 'npi' | 'providerName' | 'status' | 'reason' | 'deepPrimaryReason' | 'passCount' | 'onRoster' | 'publicBlocks' | 'priority' | 'workStatus' | 'owner' | 'closedAt';
type SortDirection = 'asc' | 'desc';
/** Whether a provider must carry any one of the selected reasons, or all of them. */
type ReasonMatch = 'any' | 'all';

/**
 * Which blocker the reason column reads. First is the first gate a provider hits
 * (always present); Last is the gate still left once everything upstream is
 * fixed (weekly workbook only); Any matches every reason a provider carries.
 */
type ReasonMode = 'first' | 'last' | 'any';

/**
 * Mutually exclusive views over the snapshot. Column filters then narrow within
 * the view, so "what kind of work am I looking at" and "which of it" stay apart.
 */
type QueueView = 'open' | 'maintenance' | 'publishing' | 'excluded' | 'history' | 'all';

const QUEUE_VIEWS: { key: QueueView; label: string; title: string }[] = [
  {
    key: 'open',
    label: 'Open work',
    title: 'Blocked providers on the roster whose work item is neither closed nor excluded. Choosing a tier, a deep finding, a last-blocker reason or a department also brings in providers who have slots, because those describe providers usually bookable somewhere.'
  },
  {
    key: 'maintenance',
    label: 'Maintenance / onboarding',
    title: 'Providers whose only findings are no active department or inactive departments. Mixed active issues stay in Open work.'
  },
  {
    key: 'publishing',
    label: 'Publishing',
    title: 'On the roster and bookable in Epic, but none of the slots are at a department on the provider\'s published Marketing Master locations, so Find Care cannot show them. Symplr location work, not Epic work.'
  },
  { key: 'excluded', label: 'Excluded', title: 'Items with an exclusion in force today. They return to Open work when the end date passes.' },
  {
    key: 'history',
    label: 'History',
    title: 'Closed items: verified Resolved by a person, or Auto-Resolved when a rescan showed the provider bookable. A rescan that finds one blocked again reopens it.'
  },
  { key: 'all', label: 'All providers', title: 'Everything in the snapshot, with only the column filters applied.' }
];

/** Names a single filter so a facet count can leave its own control out. */
type FilterKey =
  | 'view' | 'priority' | 'status' | 'reason' | 'deep' | 'roster' | 'symplr' | 'public' | 'publicSite'
  | 'workStatus' | 'exclusion' | 'covered' | 'team' | 'assignedTo' | 'department' | 'search';

const NO_SKIP: FilterKey[] = [];

const PRIORITY_VALUES = ['P1', 'P2', 'P3', NONE];

/**
 * Filters whose selection lifts Open work's has-slots rule, because each of them
 * describes a provider who is usually bookable somewhere - that is exactly why
 * the Level 1 cascade did not flag them. A last-blocker reason is in the list
 * only while the reason column is in Last mode.
 */
const LIFTS_BLOCKED_ONLY: FilterKey[] = ['priority', 'deep', 'department', 'publicSite'];

const INACTIVITY_REASONS = new Set([
  'NO-ACTIVE-DEPARTMENTS',
  'PROVIDER-INACTIVE-IN-DEPARTMENT'
]);

const isMaintenanceOnly = (provider: EpicBlockProviderRow): boolean =>
  provider.maintenanceOnly ??
  (provider.distinctReasons.length > 0 &&
    provider.distinctReasons.every((reason) => INACTIVITY_REASONS.has(reason)));

const NON_BLOCKING_REASONS = new Set([
  'HAS-SLOTS',
  'FULLY-BOOKED',
  'OK-BUT-NO-OPEN-SLOTS',
  'RULES-PASSED-SLOT-CHECK-SKIPPED'
]);
/** Epic's rule evaluation order, per the workbook glossary. */
const CASCADE_ORDER = [
  'PROVIDER-INACTIVE-IN-DEPARTMENT',
  'NO-ACTIVE-DEPARTMENTS',
  'DEPARTMENT-FLAG-OFF',
  'PROVIDER-FLAG-OFF',
  'SYSTEM-NOT-ENABLED',
  'VISIT-TYPE-NOT-SCHEDULABLE',
  'OUTSIDE-DAYS-OUT-WINDOW',
  'NO-FUTURE-TEMPLATE',
  'BLOCKED-OR-RESTRICTED'
];
const NOTHING_BLOCKING = '__nothing-blocking__';

const cascadeRank = (code: string): number => {
  const index = CASCADE_ORDER.indexOf(code);
  return index === -1 ? CASCADE_ORDER.length : index;
};

/**
 * Epic's first/last gate can be a pairing that passed (e.g. fully booked) while
 * another pairing is blocked; the Blocking column names the rule instead.
 */
const blockerOf = (provider: EpicBlockProviderRow, gate: string, pick: 'first' | 'last'): string => {
  if (!NON_BLOCKING_REASONS.has(gate)) return gate;
  const blocking = (provider.distinctReasons || [])
    .filter(reason => !NON_BLOCKING_REASONS.has(reason))
    .sort((a, b) => cascadeRank(a) - cascadeRank(b));
  if (blocking.length === 0) return NOTHING_BLOCKING;
  return pick === 'first' ? blocking[0] : blocking[blocking.length - 1];
};

const PAGE_SIZE = 50;
// v2: the filter model moved from toggles to views + column filters (2026-10).
const VIEW_STATE_KEY = 'providerValidator.providerQueue.viewState.v2';
const SCROLL_STATE_KEY = 'providerValidator.providerQueue.scrollY';

interface AuditViewState {
  searchTerm: string;
  view: QueueView;
  hidden: HiddenFilters;
  reasonMode: ReasonMode;
  anyReasonMatch: ReasonMatch;
  departmentFilter: string;
  applyExclusions: boolean;
  snapshotDate: string;
  sortField: SortField;
  sortDirection: SortDirection;
  currentPage: number;
}

const loadViewState = (): Partial<AuditViewState> => {
  try {
    return JSON.parse(sessionStorage.getItem(VIEW_STATE_KEY) || '{}');
  } catch {
    return {};
  }
};

/** Hides every tier except the chosen ones; no tiers means no priority filter. */
const hiddenForTiers = (tiers: string[]): string[] =>
  tiers.length === 0 ? [] : PRIORITY_VALUES.filter((value) => !tiers.includes(value));

/**
 * Filters requested in the URL, which win over whatever the last visit left in
 * session storage. This is what makes a tile on the executive dashboard land on
 * the providers it counted rather than on the previous visitor's filters.
 */
const readViewOverrides = (saved: Partial<AuditViewState>): Partial<AuditViewState> => {
  const params = new URLSearchParams(window.location.search);
  const overrides: Partial<AuditViewState> = {};

  const department = params.get('department');
  if (department) {
    // Arriving from the department queue means "show me these providers": a
    // department filter lifts Open work's has-slots and closed rules, because a
    // department's providers are often bookable elsewhere and a Level 2 finding
    // is not a block at all.
    overrides.departmentFilter = department;
    overrides.view = 'open';
    overrides.currentPage = 1;
  }

  const requested = params.get('priority');
  if (requested) {
    const tiers = requested
      .split(',')
      .map((tier) => tier.trim().toUpperCase())
      .filter((tier): tier is PriorityTier => tier === 'P1' || tier === 'P2' || tier === 'P3');
    if (tiers.length > 0) {
      overrides.hidden = { ...(saved.hidden || {}), priority: hiddenForTiers(tiers) };
      overrides.view = 'open';
      overrides.currentPage = 1;
    }
  }

  if (params.get('maintenanceOnly') === 'true') {
    overrides.view = 'maintenance';
    overrides.currentPage = 1;
  }

  if (params.get('view') === 'publishing') {
    overrides.view = 'publishing';
    overrides.currentPage = 1;
  }

  return overrides;
};

const saveViewState = (state: AuditViewState): void => {
  try {
    sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore storage write failures */
  }
};

const loadScrollY = (): number => {
  const raw = sessionStorage.getItem(SCROLL_STATE_KEY);
  const y = raw ? Number(raw) : 0;
  return Number.isFinite(y) ? y : 0;
};

const saveScrollY = (y: number): void => {
  try {
    sessionStorage.setItem(SCROLL_STATE_KEY, String(y));
  } catch {
    /* ignore storage write failures */
  }
};

const layerFlag = (value: boolean | null): string => {
  if (value === null) return '–';
  return value ? 'OK' : 'FAIL';
};

const ProviderQueue: React.FC = () => {
  const savedView = useRef((() => {
    const saved = loadViewState();
    return { ...saved, ...readViewOverrides(saved) };
  })()).current;
  const lists = useWorkQueueLists();
  const [providers, setProviders] = useState<EpicBlockProviderRow[]>([]);
  const [snapshots, setSnapshots] = useState<SnapshotManifest[]>([]);
  const [snapshotDate, setSnapshotDate] = useState<string>(savedView.snapshotDate ?? 'latest');
  const [activeSnapshot, setActiveSnapshot] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<'xlsx' | 'api'>('xlsx');
  const [searchTerm, setSearchTerm] = useState(savedView.searchTerm ?? '');
  const [view, setView] = useState<QueueView>(savedView.view ?? 'open');
  const [hidden, setHidden] = useState<HiddenFilters>(savedView.hidden ?? {});
  const [reasonMode, setReasonMode] = useState<ReasonMode>(savedView.reasonMode ?? 'first');
  const [anyReasonMatch, setAnyReasonMatch] = useState<ReasonMatch>(savedView.anyReasonMatch ?? 'any');
  const [departmentFilter, setDepartmentFilter] = useState(savedView.departmentFilter ?? 'ALL');
  const [departmentLabel, setDepartmentLabel] = useState<string | null>(null);
  const [applyExclusions, setApplyExclusions] = useState(savedView.applyExclusions ?? true);
  const [exclusions, setExclusions] = useState<ExcludedDepartment[]>([]);
  const [priorityDefinitions, setPriorityDefinitions] = useState<PriorityDefinition[]>([]);
  const [reasonGlossary, setReasonGlossary] = useState<Record<string, CodeDefinition>>({});
  const [deepGlossary, setDeepGlossary] = useState<Record<string, CodeDefinition>>({});
  const [statusGlossary, setStatusGlossary] = useState<Record<string, CodeDefinition>>({});
  const [sortField, setSortField] = useState<SortField>(savedView.sortField ?? 'providerName');
  const [sortDirection, setSortDirection] = useState<SortDirection>(savedView.sortDirection ?? 'asc');
  const [currentPage, setCurrentPage] = useState(savedView.currentPage ?? 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [expandedNpi, setExpandedNpi] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, EpicBlockDetail>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [notesByNpi, setNotesByNpi] = useState<Record<string, WorkQueueNote[]>>({});
  const [notesLoading, setNotesLoading] = useState<string | null>(null);
  const [delta, setDelta] = useState<SnapshotDelta | null>(null);
  const [deltaCategory, setDeltaCategory] = useState<DeltaCategory | null>(null);
  const requestAbortRef = useRef<AbortController | null>(null);
  const pendingScrollRef = useRef<number | null>(loadScrollY());
  const navigate = useNavigate();
  const environment = useDataEnvironment();

  /**
   * The snapshot picker's value is `latest` or `source|date`: a date alone is not
   * unique, because the weekly workbook and the nightly scan can share one.
   * A bare date (saved before this format) resolves against the snapshot list.
   */
  const parseSnapshotChoice = (
    value: string,
    list: SnapshotManifest[] = snapshots
  ): { date: string; source: 'xlsx' | 'api' } | null => {
    if (value === 'latest') return null;
    const [first, second] = value.split('|');
    if (second) return { date: second, source: first === 'api' ? 'api' : 'xlsx' };
    return { date: first, source: list.find((snapshot) => snapshot.snapshotDate === first)?.source ?? 'xlsx' };
  };

  useEffect(() => {
    // Snapshots first: a dated choice needs the list to know its producer.
    (async () => {
      const list = await loadSnapshots();
      const dated = parseSnapshotChoice(snapshotDate, list);
      const choice = dated ? `${dated.source}|${dated.date}` : 'latest';
      if (choice !== snapshotDate) setSnapshotDate(choice);
      loadProviders(choice, true, list);
    })();
    loadGlossary();
    return () => {
      saveScrollY(window.scrollY);
      requestAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveViewState({
      searchTerm, view, hidden, reasonMode, anyReasonMatch, departmentFilter, applyExclusions,
      snapshotDate, sortField, sortDirection, currentPage
    });
  }, [searchTerm, view, hidden, reasonMode, anyReasonMatch, departmentFilter, applyExclusions, snapshotDate, sortField, sortDirection, currentPage]);

  useEffect(() => {
    if (departmentFilter === 'ALL') {
      setDepartmentLabel(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/epic-blocks/departments/${departmentFilter}`)
      .then((response) => {
        if (!cancelled) setDepartmentLabel(response.data.department?.departmentName ?? null);
      })
      .catch(() => {
        // The id alone still identifies the filter; a missing name is cosmetic.
        if (!cancelled) setDepartmentLabel(null);
      });
    return () => { cancelled = true; };
  }, [departmentFilter]);

  useEffect(() => {
    if (pendingScrollRef.current !== null && !loading && providers.length > 0) {
      const y = pendingScrollRef.current;
      pendingScrollRef.current = null;
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [loading, providers]);

  const loadSnapshots = async (): Promise<SnapshotManifest[]> => {
    try {
      const response = await axios.get('/api/epic-blocks/snapshots');
      const list: SnapshotManifest[] = response.data.snapshots || [];
      setSnapshots(list);
      return list;
    } catch (err) {
      console.error('Error loading Epic block snapshots:', err);
      return [];
    }
  };

  const loadGlossary = async () => {
    try {
      const response = await axios.get('/api/epic-blocks/reasons');
      const byCode = (list: CodeDefinition[] = []) =>
        list.reduce((acc, entry) => {
          acc[entry.code] = entry;
          return acc;
        }, {} as Record<string, CodeDefinition>);
      setReasonGlossary(byCode(response.data.reasons));
      setDeepGlossary(byCode(response.data.deepReasons));
      setStatusGlossary(byCode(response.data.statuses));
    } catch (err) {
      console.error('Error loading Epic block glossary:', err);
    }
  };

  /** Description, our remediation, and the Epic record item the check maps to. */
  const glossaryTooltip = (entry: CodeDefinition | undefined, extra?: string): string | undefined => {
    if (!entry && !extra) return undefined;
    return [
      extra,
      entry?.description,
      entry?.remediation && `What to do: ${entry.remediation}`,
      entry?.owner && `Owner: ${entry.owner}`,
      entry?.test && `Test applied: ${entry.test}`,
      entry?.epicReference && `Epic reference: ${entry.epicReference}`
    ]
      .filter(Boolean)
      .join('\n\n');
  };

  const loadDelta = async (to: string | null, source: 'xlsx' | 'api') => {
    try {
      const response = await axios.get('/api/epic-blocks/delta', {
        params: to ? { to, source } : undefined
      });
      setDelta(response.data);
    } catch (err) {
      console.error('Error loading Epic block delta:', err);
      setDelta(null);
    }
  };

  const loadProviders = async (choice: string, preservePage = false, list: SnapshotManifest[] = snapshots) => {
    requestAbortRef.current?.abort();
    const abortController = new AbortController();
    requestAbortRef.current = abortController;
    const dated = parseSnapshotChoice(choice, list);

    setLoading(true);
    setError(null);
    setDelta(null);

    try {
      const response = await axios.get('/api/epic-blocks/providers', {
        // 'latest' is sent without a source so the server returns the merged
        // current state; a specific date has to name its producer, or a snapshot
        // written by the nightly scan would be looked up as a workbook snapshot
        // and come back empty.
        params: dated
          ? { snapshotDate: dated.date, source: dated.source }
          : { snapshotDate: 'latest' },
        signal: abortController.signal
      });
      if (abortController.signal.aborted) return;
      // The merged view's date is the nightly overlay when there is one, so its
      // movement has to be measured on that feed, not on the weekly workbook.
      const source: 'xlsx' | 'api' = dated
        ? dated.source
        : response.data.provenance?.overlaySnapshotDate ? 'api' : 'xlsx';
      setProviders(response.data.providers || []);
      setExclusions(response.data.exclusions || []);
      setPriorityDefinitions(response.data.priorityDefinitions || []);
      setActiveSnapshot(response.data.snapshotDate || null);
      setActiveSource(source);
      setInitialLoadDone(true);
      loadDelta(response.data.snapshotDate || null, source);
      if (!preservePage) setCurrentPage(1);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      console.error('Error loading Epic block data:', err);
      setError(err.response?.data?.error || 'Failed to load Epic block data');
      setProviders([]);
    } finally {
      if (requestAbortRef.current === abortController) {
        requestAbortRef.current = null;
        if (!abortController.signal.aborted) setLoading(false);
      }
    }
  };

  const loadNotes = async (npi: string) => {
    setNotesLoading(npi);
    try {
      const response = await axios.get(`/api/epic-blocks/notes/${npi}`);
      setNotesByNpi(prev => ({ ...prev, [npi]: response.data.notes || [] }));
    } catch (err) {
      console.error('Error loading work-queue notes:', err);
    } finally {
      setNotesLoading(null);
    }
  };

  /** Saves a note; resolves to an error message, or null once saved. */
  const submitNote = async (provider: EpicBlockProviderRow, submission: WorkItemSubmission): Promise<string | null> => {
    try {
      const response = await axios.post(`/api/epic-blocks/notes/${provider.npi}`, submission);
      const note: WorkQueueNote = response.data.note;
      setNotesByNpi(prev => ({ ...prev, [provider.npi]: [note, ...(prev[provider.npi] || [])] }));
      // The new note is the latest by construction, so the row's annotation can be
      // rolled forward here instead of re-fetching the whole snapshot.
      setProviders(prev => prev.map(row =>
        row.npi === provider.npi
          ? { ...row, annotation: rollForward(row.annotation, note, lists, statusOf(row) === 'HAS-SLOTS') }
          : row
      ));
      return null;
    } catch (err: any) {
      console.error('Error saving work-queue note:', err);
      return err.response?.data?.error || 'Failed to save note';
    }
  };

  const toggleExpanded = async (npi: string) => {
    if (expandedNpi === npi) {
      setExpandedNpi(null);
      return;
    }
    setExpandedNpi(npi);
    if (!notesByNpi[npi]) loadNotes(npi);
    if (details[npi]) return;

    setDetailLoading(npi);
    try {
      const response = await axios.get(`/api/epic-blocks/providers/${npi}`, {
        // The merged view's detail is the merged record; a dated view needs that
        // snapshot's own pairings, which the server only returns given its source.
        params: snapshotDate !== 'latest' && activeSnapshot
          ? { snapshotDate: activeSnapshot, source: activeSource }
          : undefined
      });
      setDetails(prev => ({ ...prev, [npi]: response.data }));
    } catch (err) {
      console.error('Error loading Epic block detail:', err);
    } finally {
      setDetailLoading(null);
    }
  };

  const handleSnapshotChange = (date: string) => {
    setSnapshotDate(date);
    setExpandedNpi(null);
    setDetails({});
    loadProviders(date);
  };

  const handleSort = (field: SortField) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'passCount' || field === 'publicBlocks' || field === 'closedAt' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // With exclusions on, every count and filter reads the effective status so the
  // headline numbers and the rows below can never disagree.
  const statusOf = (p: EpicBlockProviderRow) =>
    applyExclusions ? p.effectiveStatus ?? p.status : p.status;

  const hasFurthestData = providers.some(p => !!p.furthestReason);
  const hasDeepData = providers.some(p => !!p.deepPrimaryReason);
  const hasCoverage = providers.some(p => !!p.coveredByDepartmentItem);
  const COLUMN_COUNT = 11 + (hasDeepData ? 1 : 0);
  // Last-blocker data comes only from the weekly workbook; without it the mode
  // would filter on blanks.
  const effectiveReasonMode: ReasonMode = reasonMode === 'last' && !hasFurthestData ? 'first' : reasonMode;

  /** The value(s) each filter section reads off a row. */
  const valueOf: Record<Exclude<FilterKey, 'view' | 'department' | 'search'>, (p: EpicBlockProviderRow) => string | string[]> = {
    priority: p => p.priority ?? NONE,
    status: p => statusOf(p),
    reason: p =>
      effectiveReasonMode === 'any'
        ? p.distinctReasons || []
        : effectiveReasonMode === 'last'
          ? p.furthestReason ? blockerOf(p, p.furthestReason, 'last') : NONE
          : blockerOf(p, p.primaryReason, 'first'),
    deep: p => p.deepPrimaryReason || NONE,
    roster: p => (p.onRoster === false ? 'Off roster' : 'On roster'),
    symplr: p => (p.directoryActive === true ? 'Active' : p.directoryActive === false ? 'Inactive' : 'Unknown'),
    public: p => ((p.blockedPublicDepartments?.length ?? 0) > 0 ? 'Blocked at a public location' : 'No public block'),
    publicSite: p => (p.publicReach ? PUBLIC_REACH[p.publicReach].label : NOT_BOOKABLE_IN_EPIC),
    workStatus: p => p.annotation?.status ?? NONE,
    exclusion: p => {
      const a = p.annotation;
      if (!a?.exclusionType) return NONE;
      return `${a.excludedFromUnresolved ? 'active' : 'ended'}:${a.exclusionType}`;
    },
    covered: p => (p.coveredByDepartmentItem ? 'Covered by a department item' : 'Not covered'),
    team: p => p.annotation?.assignedTeam ?? NONE,
    assignedTo: p => p.annotation?.assignedTo?.upn ?? NONE
  };

  const sectionActive = (key: FilterKey) => (hidden[key]?.length ?? 0) > 0;

  // In Any mode the reason filter matches on what is ticked rather than unticked.
  const anyModeSelected = effectiveReasonMode === 'any' && sectionActive('reason')
    ? tallyOptions(providers, valueOf.reason).map(option => option.value)
      .filter(code => !hidden.reason.includes(code))
    : [];

  /** Whether a row is in a view, given which of Open work's rules are lifted. */
  const inView = (p: EpicBlockProviderRow, which: QueueView, liftBlocked: boolean, liftClosed: boolean): boolean => {
    const a = p.annotation;
    switch (which) {
      case 'open':
        if (a?.addressed && !liftClosed) return false;
        if (a?.excludedFromUnresolved) return false;
        // A provider dropped by the current roster definition is still active in
        // the directory, so roster membership - not Symplr activity - decides.
        if (p.onRoster === false) return false;
        // A P2 provider reports HAS-SLOTS by definition, so this rule would remove
        // the tier from its own view; the lifting filters are the same case.
        return liftBlocked || statusOf(p) !== 'HAS-SLOTS';
      case 'maintenance':
        return isMaintenanceOnly(p) && !a?.addressed && !a?.excludedFromUnresolved;
      case 'publishing':
        return p.onRoster !== false && isPublishingGap(p) && !a?.addressed && !a?.excludedFromUnresolved;
      case 'excluded':
        return !!a?.excludedFromUnresolved;
      case 'history':
        return !!a?.addressed;
      default:
        return true;
    }
  };

  /**
   * One predicate for the list and for every count beside a control.
   *
   * `skip` leaves a filter out. That is what lets a column filter report how many
   * rows each value would actually show instead of how many exist in the
   * snapshot. `forView` evaluates against another tab, for the tab counts.
   */
  const matchesFilters = (
    p: EpicBlockProviderRow,
    skip: FilterKey[] = NO_SKIP,
    forView: QueueView = view
  ): boolean => {
    const off = (key: FilterKey) => skip.includes(key);
    const departmentActive = !off('department') && departmentFilter !== 'ALL';
    const lifts = [...LIFTS_BLOCKED_ONLY, ...(effectiveReasonMode === 'last' ? ['reason' as FilterKey] : [])];
    // Selecting a lifting filter lifts the has-slots rule, so its own count has to
    // lift it too - otherwise a value reads zero and then returns a full page.
    const liftBlocked = lifts.some(key => sectionActive(key) && !off(key)) ||
      departmentActive || skip.some(key => lifts.includes(key));

    if (!off('view') && !inView(p, forView, liftBlocked, departmentActive)) return false;

    for (const key of Object.keys(valueOf) as (keyof typeof valueOf)[]) {
      if (off(key) || !sectionActive(key)) continue;
      // Publishing providers have Epic slots, so a tier filter could only empty the tab.
      if (key === 'priority' && forView === 'publishing') continue;
      const value = valueOf[key](p);
      if (key === 'reason' && effectiveReasonMode === 'any') {
        // Every reason a provider carries, not just the one that won the rollup.
        const carried = value as string[];
        const matched = anyReasonMatch === 'all'
          ? anyModeSelected.every(code => carried.includes(code))
          : anyModeSelected.some(code => carried.includes(code));
        if (!matched) return false;
        continue;
      }
      if (!shown(hidden[key], value as string)) return false;
    }

    // Blocked and deep-finding departments both count: a Level 2 problem lives
    // on a combo that passed every rule, so it never reaches blockedDepartmentIds.
    if (departmentActive) {
      const departments = [...(p.blockedDepartmentIds || []), ...(p.deepDepartmentIds || [])];
      if (!departments.includes(departmentFilter)) return false;
    }
    if (!off('search')) {
      const term = searchTerm.trim().toLowerCase();
      if (term) {
        const name = (p.providerName || '').toLowerCase();
        if (!name.includes(term) && !p.npi.includes(term) && !(p.serId || '').includes(term)) return false;
      }
    }
    return true;
  };

  /** Rows that survive every filter except the one the control owns. */
  const facet = (skip: FilterKey[]) => providers.filter(p => matchesFilters(p, skip));

  const reasonLabel = (code: string) =>
    code === NOTHING_BLOCKING ? 'Nothing blocking' : reasonGlossary[code]?.label || code;
  const deepLabel = (code: string) => deepGlossary[code]?.label || code;
  const assigneeNames = new Map<string, string>();
  providers.forEach(p => {
    const person = p.annotation?.assignedTo;
    if (person) assigneeNames.set(person.upn, person.displayName);
  });

  const setSection = (key: FilterKey) => (next: string[]) => {
    setHidden(prev => ({ ...prev, [key]: next }));
    setCurrentPage(1);
  };

  type SectionKey = keyof typeof valueOf;
  const SECTION_TITLES: Record<SectionKey, string> = {
    priority: 'Priority',
    status: 'Epic status',
    reason: effectiveReasonMode === 'any' ? 'Any reason carried' : effectiveReasonMode === 'last' ? 'Last blocker' : 'First blocker',
    deep: 'Deep finding',
    roster: 'Roster',
    symplr: 'Symplr',
    public: 'Public blocks',
    publicSite: 'Public site',
    workStatus: 'Work status',
    exclusion: 'Exclusion',
    covered: 'Department cover',
    team: 'Team',
    assignedTo: 'Assigned to'
  };

  const SECTION_OPTIONS: Record<SectionKey, Parameters<typeof tallyOptions>[2]> = {
    priority: {
      noneLabel: 'No tier',
      order: (a, b) => PRIORITY_VALUES.indexOf(a) - PRIORITY_VALUES.indexOf(b),
      title: v => priorityDefinitions.find(d => d.tier === v)?.description
    },
    status: { title: v => statusGlossary[v]?.description || reasonGlossary[v]?.description },
    reason: {
      label: reasonLabel,
      noneLabel: 'No last blocker recorded',
      title: v =>
        v === NOTHING_BLOCKING
          ? 'Every pairing passed the rules; nothing is blocked by configuration.'
          : [v, reasonGlossary[v]?.description].filter(Boolean).join(' - ')
    },
    deep: { label: deepLabel, noneLabel: 'No deep finding', title: v => deepGlossary[v]?.description },
    roster: {},
    symplr: {},
    public: {},
    publicSite: {
      title: v => Object.values(PUBLIC_REACH).find(reach => reach.label === v)?.description
    },
    workStatus: {
      label: v => statusLabel(lists, v),
      noneLabel: 'No notes yet',
      order: (a, b) => (a === NONE ? 1 : b === NONE ? -1 : statusRank(lists, a) - statusRank(lists, b))
    },
    exclusion: {
      label: v => {
        const split = v.indexOf(':');
        const type = exclusionLabel(lists, v.slice(split + 1));
        return v.startsWith('ended:') ? `${type} (ended)` : type;
      },
      noneLabel: 'No exclusion'
    },
    covered: {},
    team: { label: v => teamLabel(lists, v), noneLabel: 'No team' },
    assignedTo: { label: v => assigneeNames.get(v) ?? v, noneLabel: 'Unassigned' }
  };

  const sectionFor = (key: SectionKey, withTitle = false): FilterSection => ({
    key,
    title: withTitle ? SECTION_TITLES[key] : undefined,
    hidden: hidden[key] || [],
    onChange: setSection(key),
    options: tallyOptions(facet([key]), valueOf[key], { hidden: hidden[key], ...SECTION_OPTIONS[key] })
  });

  const filterFor = (label: string, keys: SectionKey[], field?: SortField, extra?: ReactNode) => (
    <ColumnFilter
      label={label}
      sections={keys.map(key => sectionFor(key, keys.length > 1))}
      sort={field ? {
        direction: sortField === field ? sortDirection : null,
        onSort: direction => { setSortField(field); setSortDirection(direction); setCurrentPage(1); }
      } : undefined}
    >
      {extra}
    </ColumnFilter>
  );

  const excludedOnlyCount = providers.filter(
    p => (p.excludedDepartmentIds?.length ?? 0) > 0 &&
      (p.effectiveBlockedDepartmentIds?.length ?? 0) === 0 &&
      p.effectiveStatus !== 'NO-SCHEDULABLE-DEPARTMENTS'
  ).length;
  const noSchedulableCount = providers.filter(
    p => p.effectiveStatus === 'NO-SCHEDULABLE-DEPARTMENTS'
  ).length;
  const priorityCounts = facet(['priority']).reduce((acc, p) => {
    if (p.priority) acc[p.priority] = (acc[p.priority] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const viewCounts = QUEUE_VIEWS.reduce((acc, { key }) => {
    acc[key] = providers.filter(p => matchesFilters(p, NO_SKIP, key)).length;
    return acc;
  }, {} as Record<QueueView, number>);

  /** Tiers the priority filter currently shows; empty when it is not filtering. */
  const selectedTiers = (hidden.priority?.length ?? 0) > 0
    ? (['P1', 'P2', 'P3'] as PriorityTier[]).filter(tier => !hidden.priority.includes(tier))
    : [];

  const togglePriority = (tier: PriorityTier) => {
    const next = selectedTiers.includes(tier) ? selectedTiers.filter(t => t !== tier) : [...selectedTiers, tier];
    setSection('priority')(hiddenForTiers(next));
  };

  const changeView = (next: QueueView) => {
    setView(next);
    setCurrentPage(1);
    // History reads newest closure first; the other views keep the chosen sort.
    if (next === 'history') {
      setSortField('closedAt');
      setSortDirection('desc');
    } else if (sortField === 'closedAt') {
      setSortField('providerName');
      setSortDirection('asc');
    }
  };

  const changeReasonMode = (mode: ReasonMode) => {
    // The same code means a different thing as a first or last blocker, so a
    // filter set in one mode is not carried into another.
    setReasonMode(mode);
    setHidden(prev => ({ ...prev, reason: [] }));
    setCurrentPage(1);
  };

  // "No results" is often the saved view rather than the data, so reset has to
  // cover every control or it cannot get the user unstuck.
  const filtersAreDefault =
    view === 'open' &&
    !searchTerm &&
    departmentFilter === 'ALL' &&
    applyExclusions &&
    !(Object.keys(valueOf) as SectionKey[]).some(sectionActive);

  const resetFilters = () => {
    setView('open');
    setHidden({});
    setSearchTerm('');
    setDepartmentFilter('ALL');
    setReasonMode('first');
    setAnyReasonMatch('any');
    setApplyExclusions(true);
    if (sortField === 'closedAt') setSortField('providerName');
    setCurrentPage(1);
  };

  /** Every filter narrowing the list right now, each removable on its own. */
  const activeChips: { key: string; label: string; remove: () => void }[] = [
    ...(departmentFilter !== 'ALL'
      ? [{ key: 'department', label: `Department: ${departmentLabel || departmentFilter}`, remove: () => setDepartmentFilter('ALL') }]
      : []),
    ...(searchTerm ? [{ key: 'search', label: `Search: "${searchTerm}"`, remove: () => setSearchTerm('') }] : []),
    ...(!applyExclusions
      ? [{ key: 'exclusions', label: 'Counting blocks on never-bookable departments', remove: () => setApplyExclusions(true) }]
      : []),
    ...(Object.keys(valueOf) as SectionKey[]).filter(sectionActive).map(key => {
      const options = tallyOptions(facet([key]), valueOf[key], { hidden: hidden[key], ...SECTION_OPTIONS[key] });
      const detail = key === 'priority'
        ? selectedTiers.join(' + ') || describeHidden(hidden[key], options)
        : key === 'reason' && effectiveReasonMode === 'any'
          ? `${anyReasonMatch === 'all' ? 'all of' : 'any of'} ${anyModeSelected.length} reason${anyModeSelected.length === 1 ? '' : 's'}`
          : describeHidden(hidden[key], options);
      return { key, label: `${SECTION_TITLES[key]}: ${detail}`, remove: () => setSection(key)([]) };
    })
  ];

  const getSortedProviders = () => {
    const filtered = providers.filter(p => matchesFilters(p));

    return [...filtered].sort((a, b) => {
      // Work-queue status sorts by the configured workflow order, not alphabetically.
      const pick = (row: EpicBlockProviderRow): any => {
        switch (sortField) {
          case 'status': return statusOf(row);
          case 'priority': return row.priority ?? null;
          case 'reason': {
            const value = valueOf.reason(row);
            if (Array.isArray(value)) return row.primaryReason;
            return value === NONE || value === NOTHING_BLOCKING ? null : reasonLabel(value);
          }
          case 'deepPrimaryReason': return row.deepPrimaryReason ?? null;
          case 'onRoster': return row.onRoster !== false;
          case 'publicBlocks': return row.blockedPublicDepartments?.length ?? 0;
          case 'workStatus': return row.annotation ? statusRank(lists, row.annotation.status) : null;
          case 'owner':
            return row.annotation?.assignedTo?.displayName ??
              (row.annotation?.assignedTeam ? teamLabel(lists, row.annotation.assignedTeam) : null);
          case 'closedAt': return row.annotation?.closedAt ?? null;
          case 'passCount': return row.passCount;
          case 'npi': return row.npi;
          default: return row.providerName ?? null;
        }
      };
      const aVal: any = pick(a);
      const bVal: any = pick(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      return 0;
    });
  };

  const sortedProviders = getSortedProviders();
  const totalPages = Math.max(1, Math.ceil(sortedProviders.length / PAGE_SIZE));
  const visibleProviders = sortedProviders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    // Only clamp once a snapshot has loaded, otherwise the empty first render
    // would drop a restored page number back to 1 before the rows exist.
    if (initialLoadDone && currentPage > totalPages) setCurrentPage(totalPages);
  }, [initialLoadDone, currentPage, totalPages]);

  const activeManifest = snapshots.find(s => s.snapshotDate === activeSnapshot && s.source === activeSource);

  return (
    <div className="provider-search-container">
      <GuideBanner storageKey="providerQueue" section="provider-queue">
        <strong>This list opens on Open work.</strong> Start from the P1/P2/P3 buttons, then narrow
        with the ▽ filter in any column header. Switch the <em>Blocking</em> column to{' '}
        <em>Last blocker</em> when you want to know what is actually left to fix.
      </GuideBanner>

      <div className="intro-section">
        <h1>
          Provider Work Queue
          <span className="info-tooltip">
            <span className="info-icon">ⓘ</span>
            <span className="tooltip-text">
              This is a point-in-time snapshot from the daily Epic Open Scheduling diagnostic, not a live call.
              <br/><br/>
              It can disagree with the live slot check on the provider detail page if Epic configuration changed after the snapshot was produced.
            </span>
          </span>
          <GuideLink section="provider-queue" label="How to work this list" spaced />
        </h1>
        <p className="subtitle">
          Epic configuration that is preventing Open Scheduling availability from being visible.
          {activeSnapshot && (
            <> Showing snapshot <strong>{activeSnapshot}</strong>
              {activeManifest && ` (${activeManifest.providerCount} providers, ${activeManifest.comboCount} pairings`}
              {activeManifest?.deepComboCount ? `, ${activeManifest.deepComboCount} deep-probed` : ''}
              {activeManifest && ')'}.
            </>
          )}
        </p>
      </div>

      <div className="audit-workspace">
      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Provider name, NPI, or SER ID..."
            />
          </div>
          <div className="filter-group">
            <label>Snapshot</label>
            <select value={snapshotDate} onChange={(e) => handleSnapshotChange(e.target.value)}>
              <option value="latest">Latest</option>
              {snapshots.map(s => (
                <option key={`${s.source}_${s.snapshotDate}`} value={`${s.source}|${s.snapshotDate}`}>
                  {s.snapshotDate} ({s.source === 'api' ? 'nightly' : 'weekly'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedTiers.length > 0 && priorityDefinitions.length > 0 && (
          <div className="exclusion-note">
            Showing {selectedTiers.length === 1 ? 'tier' : 'tiers'}{' '}
            {selectedTiers.join(' + ')}.{' '}
            {priorityDefinitions
              .filter(d => selectedTiers.includes(d.tier))
              .map(d => `${d.tier}: ${d.description}`)
              .join(' ')}
            {' '}A tier also shows providers who have slots, because a P2 provider reports HAS-SLOTS by definition.
          </div>
        )}

        {exclusions.length > 0 && (
          <div className="exclusion-note">
            {applyExclusions ? 'Not counted as blocks: ' : 'Counting blocks on never-bookable departments: '}
            {exclusions.map(x => `${x.name} (${x.departmentId})${x.excludedUntil ? ` until ${x.excludedUntil}` : ''}`).join(', ')}.
            {applyExclusions && excludedOnlyCount > 0 && ` ${excludedOnlyCount} provider${excludedOnlyCount === 1 ? '' : 's'} `}
            {applyExclusions && excludedOnlyCount > 0 && `blocked only there ${excludedOnlyCount === 1 ? 'is' : 'are'} no longer counted as blocked.`}
            {applyExclusions && noSchedulableCount > 0 && (
              <>
                {' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    const others = tallyOptions(providers, valueOf.status).map(o => o.value)
                      .filter(value => value !== 'NO-SCHEDULABLE-DEPARTMENTS');
                    setView('open');
                    setSection('status')(others);
                  }}
                >
                  {noSchedulableCount} provider{noSchedulableCount === 1 ? ' has' : 's have'} no other department at all
                </button>
                {' - review separately.'}
              </>
            )}
            {' '}
            <button
              type="button"
              className="link-button"
              title={exclusions.map(x => `${x.name} (${x.departmentId})\n${x.rationale}`).join('\n\n')}
              onClick={() => { setApplyExclusions(!applyExclusions); setCurrentPage(1); }}
            >
              {applyExclusions ? 'Count them anyway' : 'Stop counting them'}
            </button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate('/settings')}>
              Edit the list
            </button>
          </div>
        )}

        <div className="filter-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={() => loadProviders(snapshotDate)}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {!filtersAreDefault && (
            <button type="button" className="btn btn-secondary" onClick={resetFilters}>
              Clear Filters
            </button>
          )}
          {priorityDefinitions.length > 0 && (
            <div className="priority-queue">
              <span className="priority-queue-label">
                Where to start:
                <GuideLink section="priority" label="Tiers" spaced />
              </span>
              <div className="priority-toggle-group" role="group" aria-label="Filter by priority tier">
                {priorityDefinitions.map(d => {
                  const active = selectedTiers.includes(d.tier);
                  return (
                    <button
                      key={d.tier}
                      type="button"
                      className={`priority-toggle priority-${d.tier.toLowerCase()} ${active ? 'priority-toggle-active' : ''}`}
                      aria-pressed={active}
                      title={d.description}
                      onClick={() => togglePriority(d.tier)}
                    >
                      <span className="priority-toggle-tier">{d.tier}</span>
                      <span className="priority-toggle-count">{priorityCounts[d.tier] || 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

        <aside className="audit-tiles" aria-label="Snapshot movement">
          {delta && delta.from
            ? DELTA_TILES.map(({ key, label, tone }) => {
                const count = delta[key].length;
                const selected = deltaCategory === key;
                const since = `${delta.from!.slice(5, 7)}/${delta.from!.slice(8, 10)}`;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`hero-tile hero-tile-${tone} ${selected ? 'hero-tile-active' : ''}`}
                    disabled={count === 0}
                    aria-pressed={selected}
                    title={`${label} between ${delta.from} and ${delta.to}`}
                    onClick={() => setDeltaCategory(selected ? null : key)}
                  >
                    <span className="hero-tile-value">{count}</span>
                    <span className="hero-tile-label">{label}</span>
                    <span className="hero-tile-caption">
                      {count === 0 ? `no change since ${since}` : `since ${since} - view providers`}
                    </span>
                  </button>
                );
              })
            : // Placeholders hold the column's height while the comparison loads,
              // so the arriving numbers cannot shove the results list down.
              DELTA_TILES.map(({ key }) => (
                <div key={key} className="hero-tile hero-tile-placeholder" aria-hidden="true" />
              ))}
        </aside>
      </div>

      {delta && deltaCategory && delta[deltaCategory].length > 0 && (
        <div className="results-section" style={{ marginBottom: '1rem' }}>
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>NPI</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {delta[deltaCategory].map((row) => (
                  <tr key={`${deltaCategory}-${row.npi}`}>
                    <td>{row.npi}</td>
                    <td><strong>{row.providerName || 'Unknown'}</strong></td>
                    <td>
                      {row.fromStatus && row.toStatus
                        ? `${row.fromStatus} → ${row.toStatus}`
                        : row.status || '–'}
                    </td>
                    <td>
                      {row.fromReasonLabel && row.toReasonLabel && row.fromReasonLabel !== row.toReasonLabel
                        ? `${row.fromReasonLabel} → ${row.toReasonLabel}`
                        : row.toReasonLabel || '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {!loading && initialLoadDone && providers.length === 0 && (
        <div className="environment-empty">
          <strong>No Epic diagnostic snapshot for {environment.toUpperCase()}.</strong>
          <div style={{ marginTop: '0.35rem' }}>
            Drop a diagnostic workbook into <code>incoming/{environment}/</code> in the
            <code> epic-diagnostics</code> container and it will be ingested automatically.
          </div>
        </div>
      )}

      {providers.length > 0 && (
        <div className="results-section">
          <div className="wq-views" role="tablist" aria-label="Queue views">
            {QUEUE_VIEWS.map(({ key, label, title }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={view === key}
                className={`wq-view ${view === key ? 'wq-view-active' : ''}`}
                title={title}
                onClick={() => changeView(key)}
              >
                {label}
                <span className="wq-view-count">{viewCounts[key]}</span>
              </button>
            ))}
            <GuideLink section="queue-filters" label="Views and filters" spaced />
          </div>

          {activeChips.length > 0 && (
            <div className="wq-chips" aria-label="Active filters">
              {activeChips.map(chip => (
                <span key={chip.key} className="wq-chip">
                  {chip.label}
                  <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => { chip.remove(); setCurrentPage(1); }}>
                    ×
                  </button>
                </span>
              ))}
              <button type="button" className="link-button" onClick={resetFilters}>
                Reset to defaults
              </button>
            </div>
          )}

          <div className="results-header">
            <p className="results-count">
              Showing {sortedProviders.length} of {providers.length} provider{providers.length !== 1 ? 's' : ''} in this snapshot
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </p>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('priority')}>
                    <span className="wq-th">Priority{getSortIcon('priority')}{filterFor('Priority', ['priority'], 'priority')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('npi')}>NPI{getSortIcon('npi')}</th>
                  <th className="sortable" onClick={() => handleSort('providerName')}>Name{getSortIcon('providerName')}</th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('status')}
                    title="Epic's verdict for the provider's best department/visit-type pairing. A provider can be Fully booked here and still blocked at another department; see Blocking."
                  >
                    <span className="wq-th">Epic status{getSortIcon('status')}{filterFor('Epic status', ['status'], 'status')}</span>
                  </th>
                  <th
                    className="sortable"
                    onClick={() => handleSort('reason')}
                    title="The rule blocking this provider. First: the first blocking rule in Epic's evaluation order. Last: what is still left to fix once everything upstream is fixed. Any: every reason a provider carries."
                  >
                    <span className="wq-th">
                      Blocking
                      <small style={{ textTransform: 'none', fontWeight: 500 }}>
                        ({effectiveReasonMode === 'last' ? 'last' : effectiveReasonMode === 'any' ? 'any' : 'first'})
                      </small>
                      {getSortIcon('reason')}
                      {filterFor('Blocking', ['reason'], 'reason', (
                        <>
                          <div className="wq-mode" role="group" aria-label="Which blocker to read">
                            {(['first', 'last', 'any'] as ReasonMode[]).map(mode => (
                              <button
                                key={mode}
                                type="button"
                                className={effectiveReasonMode === mode ? 'active' : ''}
                                disabled={mode === 'last' && !hasFurthestData}
                                title={mode === 'last' && !hasFurthestData ? 'Only the weekly workbook records a last blocker; this snapshot has none.' : undefined}
                                onClick={() => changeReasonMode(mode)}
                              >
                                {mode === 'first' ? 'First blocker' : mode === 'last' ? 'Last blocker' : 'Any carried'}
                              </button>
                            ))}
                          </div>
                          {effectiveReasonMode === 'any' && (
                            <div className="wq-mode" role="group" aria-label="How to combine reasons" style={{ marginTop: '0.35rem' }}>
                              {(['any', 'all'] as ReasonMatch[]).map(match => (
                                <button
                                  key={match}
                                  type="button"
                                  className={anyReasonMatch === match ? 'active' : ''}
                                  onClick={() => { setAnyReasonMatch(match); setCurrentPage(1); }}
                                >
                                  {match === 'any' ? 'Carries any ticked' : 'Carries all ticked'}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="wq-mode-hint">
                            {effectiveReasonMode === 'last'
                              ? 'What would still need fixing once everything upstream is fixed - the clearest view of remaining work.'
                              : effectiveReasonMode === 'any'
                                ? 'Matches every reason a provider carries, not just the one that won the rollup.'
                                : 'The first rule that blocks a provider. Where Epic\'s own first gate is a pairing that passed (for example fully booked), the first blocking rule is shown instead.'}
                          </div>
                        </>
                      ))}
                    </span>
                  </th>
                  {hasDeepData && (
                    <th
                      className="sortable"
                      onClick={() => handleSort('deepPrimaryReason')}
                      title="Level 2. Why a pairing that passed every configuration rule still returned no slots."
                    >
                      <span className="wq-th">Deep finding{getSortIcon('deepPrimaryReason')}{filterFor('Deep finding', ['deep'], 'deepPrimaryReason')}</span>
                    </th>
                  )}
                  <th className="sortable" onClick={() => handleSort('onRoster')}>
                    <span className="wq-th">Roster{getSortIcon('onRoster')}{filterFor('Roster', ['roster', 'symplr'], 'onRoster')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('publicBlocks')}>
                    <span className="wq-th">Public blocks{getSortIcon('publicBlocks')}{filterFor('Public blocks', ['public', 'publicSite'], 'publicBlocks')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('passCount')} title="Pairings that passed, out of all pairings">
                    Passing{getSortIcon('passCount')}
                  </th>
                  <th className="sortable" onClick={() => handleSort(view === 'history' ? 'closedAt' : 'workStatus')}>
                    <span className="wq-th">
                      Work item{getSortIcon(view === 'history' ? 'closedAt' : 'workStatus')}
                      {filterFor('Work item', hasCoverage ? ['workStatus', 'exclusion', 'covered'] : ['workStatus', 'exclusion'], 'workStatus')}
                    </span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('owner')}>
                    <span className="wq-th">Owner{getSortIcon('owner')}{filterFor('Owner', ['team', 'assignedTo'], 'owner')}</span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProviders.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="wq-muted">
                      No providers match this view and these filters.{' '}
                      {!filtersAreDefault && (
                        <button type="button" className="link-button" onClick={resetFilters}>
                          Reset to defaults
                        </button>
                      )}
                    </td>
                  </tr>
                )}
                {visibleProviders.map((provider) => {
                  const shownStatus = statusOf(provider);
                  const statusAdjusted = applyExclusions && shownStatus !== provider.status;
                  return (
                  <React.Fragment key={provider.npi}>
                    <tr className={expandedNpi === provider.npi ? 'wq-row-expanded' : undefined}>
                      <td>
                        {provider.priority ? (
                          <span
                            className={`priority-pill priority-${provider.priority.toLowerCase()}`}
                            title={provider.priorityReason || undefined}
                          >
                            {provider.priority}
                          </span>
                        ) : (
                          <span
                            style={{ color: '#9aa5b1' }}
                            title={provider.priorityReason || 'Not queued: no patient-facing block.'}
                          >
                            –
                          </span>
                        )}
                        {isDepartmentConfig(provider) && (
                          <button
                            type="button"
                            className="wq-dept-config"
                            title="Held back by the department-level Open Scheduling flag. One change on the department clears every provider behind it, so work it in the Department Queue. The department is not published for this provider yet, so check it is meant to go live first."
                            onClick={() => {
                              saveScrollY(window.scrollY);
                              navigate(`/departments?ids=${(provider.effectiveBlockedDepartmentIds || provider.blockedDepartmentIds || []).join(',')}`);
                            }}
                          >
                            Dept config
                          </button>
                        )}
                      </td>
                      <td>{provider.npi}</td>
                      <td><strong>{provider.providerName || 'Unknown'}</strong></td>
                      <td>
                        <span
                          className="glossary-term"
                          title={glossaryTooltip(
                            statusGlossary[shownStatus] || reasonGlossary[shownStatus],
                            statusAdjusted
                              ? `Diagnostic reported ${provider.status}; blocked only on excluded department(s) ${(provider.excludedDepartmentIds || []).join(', ')}.`
                              : undefined
                          )}
                          style={{
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          backgroundColor: shownStatus === 'HAS-SLOTS' ? '#d4edda' : '#f8d7da',
                          color: shownStatus === 'HAS-SLOTS' ? '#155724' : '#721c24'
                        }}>
                          {shownStatus}{statusAdjusted ? ' *' : ''}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          // The cell follows the column's mode so what is shown is what is filtered.
                          const gate = effectiveReasonMode === 'last' ? provider.furthestReason : provider.primaryReason;
                          if (!gate) {
                            return <span className="wq-muted" title="No last blocker recorded for this provider.">–</span>;
                          }
                          const code = effectiveReasonMode === 'any'
                            ? gate
                            : blockerOf(provider, gate, effectiveReasonMode === 'last' ? 'last' : 'first');
                          if (code === NOTHING_BLOCKING) {
                            return <span className="wq-muted" title={`Nothing is blocked by configuration. Epic's gate: ${reasonLabel(gate)}.`}>–</span>;
                          }
                          const substituted = code !== gate
                            ? `Epic's ${effectiveReasonMode === 'last' ? 'last' : 'first'} gate was ${reasonLabel(gate)}, a pairing that passed; this is the ${effectiveReasonMode === 'last' ? 'last' : 'first'} rule that blocks.`
                            : undefined;
                          const extra = substituted ?? (effectiveReasonMode === 'last'
                            ? code === provider.primaryReason
                              ? 'The only gate this provider hits.'
                              : `Still to fix after the first blocker (${reasonLabel(provider.primaryReason)}).`
                            : undefined);
                          const others = effectiveReasonMode === 'any'
                            ? (provider.distinctReasons || []).filter(reason => reason !== code)
                            : [];
                          return (
                            <>
                              <span className="glossary-term" title={glossaryTooltip(reasonGlossary[code], extra)}>
                                {code === provider.primaryReason ? provider.primaryReasonLabel || reasonLabel(code) : reasonLabel(code)}
                              </span>
                              {others.length > 0 && (
                                <div className="wq-sub" title={others.map(reasonLabel).join('\n')}>
                                  +{others.length} more
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      {hasDeepData && (
                        <td>
                          {provider.deepPrimaryReason ? (
                            <span
                              className="glossary-term"
                              title={glossaryTooltip(
                                deepGlossary[provider.deepPrimaryReason],
                                `${provider.deepComboCount ?? 0} pairing(s) deep-probed.`
                              )}
                            >
                              {deepGlossary[provider.deepPrimaryReason]?.label || provider.deepPrimaryReason}
                            </span>
                          ) : (
                            <span style={{ color: '#9aa5b1' }} title="No pairing needed the deep probe.">–</span>
                          )}
                        </td>
                      )}
                      <td>
                        {provider.onRoster === false ? (
                          <span style={{ color: '#721c24', fontWeight: 600 }} title="Not on the Open Scheduling roster in the latest import.">Off roster</span>
                        ) : (
                          'On roster'
                        )}
                        {provider.directoryActive === false && <div className="wq-sub wq-warn">Inactive in Symplr</div>}
                      </td>
                      <td>
                        {(provider.blockedPublicDepartments?.length ?? 0) > 0 ? (
                          <span
                            style={{ color: '#b26a00', fontWeight: 700 }}
                            title={`Blocked on published department(s): ${provider.blockedPublicDepartments.join(', ')}`}
                          >
                            {provider.blockedPublicDepartments.length}
                          </span>
                        ) : (
                          '\u2013'
                        )}
                        {isPublishingGap(provider) && (
                          <div className="wq-sub wq-warn" title={PUBLIC_REACH[provider.publicReach as PublicReach].description}>
                            Not on public site
                          </div>
                        )}
                      </td>
                      <td title={`${provider.passCount} of ${provider.comboCount} pairings passed`}>
                        {provider.passCount} / {provider.comboCount}
                      </td>
                      <td>
                        <WorkItemBadges annotation={provider.annotation} lists={lists} />
                        {provider.coveredByDepartmentItem && (
                          <div
                            className="wq-sub wq-covered"
                            title={`An open department item (${statusLabel(lists, provider.coveredByDepartmentItem.status)}) already covers department ${provider.coveredByDepartmentItem.departmentId}, which is blocking this provider. Working this row individually may duplicate that effort.`}
                          >
                            Covered by dept item
                          </div>
                        )}
                      </td>
                      <td>
                        <OwnerCell annotation={provider.annotation} lists={lists} />
                      </td>
                      <td>
                        <button
                          onClick={() => toggleExpanded(provider.npi)}
                          className="btn btn-sm btn-secondary"
                          title="Blocked pairings for this provider, and the work-queue notes on it"
                        >
                          {expandedNpi === provider.npi ? 'Hide' : 'Details & Notes'}
                          {provider.annotation?.noteCount ? ` (${provider.annotation.noteCount})` : ''}
                        </button>
                        <button
                          onClick={() => { saveScrollY(window.scrollY); navigate(`/providers/${provider.npi}`); }}
                          className="btn btn-sm btn-primary"
                          style={{ marginLeft: '6px' }}
                          disabled={provider.directoryActive === false}
                          title={
                            provider.directoryActive === false
                              ? 'Inactive in Symplr, so the live directory has no detail record for this provider.'
                              : 'Open the live provider profile page'
                          }
                        >
                          View profile
                        </button>
                      </td>
                    </tr>
                    {expandedNpi === provider.npi && (
                      <tr className="wq-detail-row">
                        <td colSpan={COLUMN_COUNT}>
                          <div className="wq-panel">
                            <h4>
                              Work queue notes
                              {provider.annotation && (
                                <span style={{ fontWeight: 400, color: '#6b7480' }}>
                                  {' '}- currently {statusLabel(lists, provider.annotation.status)}
                                </span>
                              )}
                            </h4>
                            {provider.annotation?.systemProposedResolution && (
                              <div className="exclusion-note" style={{ marginBottom: '0.6rem' }}>
                                The {provider.snapshotDate} snapshot shows no block for this provider, but the
                                newest note still has it open. The next rescan closes it as Auto-Resolved; set
                                Resolved now if you have confirmed the fix.
                              </div>
                            )}
                            <WorkItemForm
                              key={provider.npi}
                              annotation={provider.annotation}
                              lists={lists}
                              placeholder="What you found, ticket number, next step..."
                              onSubmit={(submission) => submitNote(provider, submission)}
                            />
                            <NoteHistory
                              notes={notesByNpi[provider.npi]}
                              loading={notesLoading === provider.npi}
                              lists={lists}
                              emptyText="No notes yet. Be the first to annotate this provider."
                              context={(note) => (note.providerStatus ? `(${note.providerStatus})` : null)}
                            />
                          </div>
                          {detailLoading === provider.npi && <p>Loading pairings...</p>}
                          {details[provider.npi] && (
                            <div>
                              {details[provider.npi].primaryReasonDetail && (
                                <p style={{ marginTop: 0 }}>
                                  <strong>{details[provider.npi].primaryReasonDetail!.label}:</strong>{' '}
                                  {details[provider.npi].primaryReasonDetail!.description}
                                  {details[provider.npi].primaryReasonDetail!.remediation && (
                                    <><br/><em>{details[provider.npi].primaryReasonDetail!.remediation}</em></>
                                  )}
                                </p>
                              )}
                              <table className="results-table">
                                <thead>
                                  <tr>
                                    <th>Department</th>
                                    <th title="Whether a Symplr Marketing Master location publishes this department. Only Published departments count toward P1/P2.">Public location</th>
                                    <th>Visit Type</th>
                                    <th>System</th>
                                    <th>Provider Flag</th>
                                    <th>Dept Flag</th>
                                    <th>Visit Type</th>
                                    <th>Date Window</th>
                                    <th>Template</th>
                                    <th>Slots</th>
                                    <th>Reason</th>
                                    {hasDeepData && <th title="Level 2 - what the deep slot-search probe found, plus Epic's own message.">Deep Finding</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {details[provider.npi].combos.map((combo, idx) => (
                                    <tr key={`${provider.npi}-combo-${idx}`}>
                                      <td>{combo.departmentName || '–'}<br/><small>{combo.departmentId || ''}</small></td>
                                      <td>
                                        {combo.publication === 'PUBLISHED' ? (
                                          <span
                                            style={{ color: '#107C10', fontWeight: 600 }}
                                            title="On one of this provider's published Marketing Master locations, so a fix is visible to patients."
                                          >
                                            Published
                                          </span>
                                        ) : combo.publication === 'OTHER-PROVIDERS' ? (
                                          <span
                                            style={{ color: '#b26a00', fontWeight: 600 }}
                                            title="Published on another location, but not on this provider's, so a fix would not surface this provider here."
                                          >
                                            Not for this provider
                                          </span>
                                        ) : combo.publication === 'NOT-PUBLISHED' ? (
                                          <span
                                            style={{ color: '#b26a00', fontWeight: 600 }}
                                            title="No Symplr Marketing Master location publishes this department, so an Epic fix alone surfaces nothing."
                                          >
                                            Not published
                                          </span>
                                        ) : (
                                          <span style={{ color: '#9aa5b1' }} title="The location mapping was unavailable.">–</span>
                                        )}
                                      </td>
                                      <td>{combo.visitTypeName || '–'}<br/><small>{combo.visitTypeId || ''}</small></td>
                                      <td>{layerFlag(combo.systemEnabled)}</td>
                                      <td>{layerFlag(combo.providerFlagOK)}</td>
                                      <td>{layerFlag(combo.deptFlagOK)}</td>
                                      <td>{layerFlag(combo.visitTypeSchedulable)}</td>
                                      <td>{layerFlag(combo.dateWindowOK)}</td>
                                      <td>{layerFlag(combo.hasFutureTemplate)}</td>
                                      <td>{combo.slotsFound ?? '–'}</td>
                                      <td title={combo.reasonDetail?.description || ''}>
                                        {combo.reasonDetail?.label || combo.reasonCode}
                                      </td>
                                      {hasDeepData && (
                                        <td>
                                          {combo.deep?.reasonCode ? (
                                            <>
                                              <span
                                                className="glossary-term"
                                                title={glossaryTooltip(
                                                  combo.deepReasonDetail ?? deepGlossary[combo.deep.reasonCode]
                                                )}
                                              >
                                                {combo.deepReasonDetail?.label || combo.deep.reasonCode}
                                              </span>
                                              {/* Epic's own wording. Kept verbatim - it names the pool,
                                                  block or template the fix has to touch. */}
                                              {combo.deep.autoSchedErrMessage && (
                                                <div style={{ fontSize: '11px', color: '#6b7480', marginTop: '2px' }}>
                                                  Epic: {combo.deep.autoSchedErrMessage}
                                                </div>
                                              )}
                                              {combo.deep.templateSlotsFound !== null && combo.deep.templateSlotsFound > 0 && (
                                                <div style={{ fontSize: '11px', color: '#6b7480' }}>
                                                  {combo.deep.templateSlotsFound} template slot(s)
                                                  {combo.deep.firstTemplateSlotDate && `, first ${combo.deep.firstTemplateSlotDate}`}
                                                </div>
                                              )}
                                            </>
                                          ) : (
                                            <span style={{ color: '#9aa5b1' }}>–</span>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
              >
                Previous
              </button>
              <span className="page-info">Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProviderQueue;
